import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { FlaskConical, Plus, Search, Stethoscope } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { depuis, pluriel } from '../../lib/format.ts';
import { Badge, Chargement, ChampChoix, EntetePage, EtatErreur, EtatVide, LienBouton, Panneau } from '../../components/ui.tsx';
import { BadgeStatutIntervention, STATUTS_INTERVENTION } from '../../components/statuts.tsx';
import type { InterventionResume } from './types.ts';
import { OngletsInterventions } from './chantiers/commun.tsx';

const FILTRES_STATUT = [
  { valeur: 'ouvertes', libelle: 'Ouvertes (brouillon et en cours)', api: 'brouillon,en_cours' },
  ...Object.entries(STATUTS_INTERVENTION).map(([k, v]) => ({ valeur: k, libelle: v.libelle, api: k })),
];

export function PageInterventions() {
  const { peut } = useSession();
  const [params, setParams] = useSearchParams();
  const statut = params.get('statut') ?? '';
  const site = params.get('site') ?? '';
  const miennes = params.get('miennes') === '1';
  const [texte, setTexte] = useState(params.get('q') ?? '');
  const [q, setQ] = useState(texte);
  useEffect(() => { const t = setTimeout(() => setQ(texte.trim()), 300); return () => clearTimeout(t); }, [texte]);

  const changer = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
  };

  const sites = useQuery({ queryKey: ['sites'], queryFn: () => api.get<{ id: string; name: string }[]>('/api/sites') });
  const filtreApi = FILTRES_STATUT.find((f) => f.valeur === statut)?.api ?? '';
  const url = `/api/interventions?${new URLSearchParams({ ...(filtreApi ? { statut: filtreApi } : {}), ...(site ? { site } : {}), ...(miennes ? { miennes: '1' } : {}), ...(q ? { q } : {}) })}`;
  const liste = useQuery({ queryKey: ['interventions', url], queryFn: () => api.get<InterventionResume[]>(url) });
  const filtre = !!(statut || site || miennes || q);

  return (
    <div className="pile-l">
      <EntetePage titre="Interventions" sousTitre="Diagnostics guidés : symptôme, contrôles sourcés, mesures, résolution ou escalade."
        actions={peut('intervention.ecrire') && <LienBouton to="/interventions/nouvelle" variante="principal" icone={<Plus aria-hidden />}>Nouvelle intervention</LienBouton>} />
      {peut('chantier.lire') && <OngletsInterventions />}

      <div className="int-filtres" role="search" aria-label="Filtrer les interventions">
        <label className="int-recherche">
          <Search size={18} aria-hidden className="discret" />
          <span className="visuellement-cache">Rechercher une intervention</span>
          <input className="saisie" type="search" placeholder="Référence, équipement, code défaut, site" value={texte} onChange={(e) => setTexte(e.target.value)} />
        </label>
        <ChampChoix libelle="Statut" value={statut} onChange={(e) => changer('statut', e.target.value)} vide="Tous les statuts"
          options={FILTRES_STATUT.map((f) => ({ valeur: f.valeur, libelle: f.libelle }))} />
        <ChampChoix libelle="Site" value={site} onChange={(e) => changer('site', e.target.value)} vide="Tous les sites autorisés"
          options={(sites.data ?? []).map((s) => ({ valeur: s.id, libelle: s.name }))} />
        <label className="case int-case">
          <input type="checkbox" checked={miennes} onChange={(e) => changer('miennes', e.target.checked ? '1' : '')} />
          <span>Seulement les miennes</span>
        </label>
      </div>

      {liste.isLoading && <Chargement />}
      {liste.isError && <EtatErreur erreur={liste.error} reessayer={() => liste.refetch()} />}
      {liste.data && (
        <Panneau serre titre={pluriel(liste.data.length, 'intervention')}>
          {liste.data.length === 0
            ? <EtatVide icone={<Stethoscope />} titre={filtre ? 'Aucune intervention ne correspond' : 'Aucune intervention'}
                texte={filtre ? 'Modifiez les filtres ou la recherche.' : 'Démarrez un diagnostic depuis un site ou un équipement : symptôme, contexte, contrôles sourcés.'}
                action={!filtre && peut('intervention.ecrire') ? <LienBouton to="/interventions/nouvelle" variante="principal">Diagnostiquer une panne</LienBouton> : undefined} />
            : (
              <div className="liste">
                {liste.data.map((i) => (
                  <Link key={i.id} to={`/interventions/${i.id}`} className="liste-element int-ligne">
                    <span className="liste-element-corps">
                      <span className="liste-element-titre">{i.title}</span>
                      <span className="petit discret">
                        <span className="mono">{i.reference}</span> — {i.site_name}{i.equipement ? ` — ${i.equipement}` : ''}
                        {i.equipement_reference && <span className="expert-seulement"> (<span className="mono">{i.equipement_reference}</span>)</span>}
                      </span>
                    </span>
                    <span className="int-ligne-badges">
                      {i.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
                      {i.controles_a_faire > 0 && ['brouillon', 'en_cours'].includes(i.status) && (
                        <span className="expert-seulement"><Badge ton="info" icone={false}>{pluriel(i.controles_a_faire, 'contrôle à faire', 'contrôles à faire')}</Badge></span>
                      )}
                      <BadgeStatutIntervention statut={i.status} />
                    </span>
                    <span className="tres-petit discret expert-seulement masque-etroit int-ligne-date">{i.intervenant ? `${i.intervenant}, ` : ''}{depuis(i.updated_at)}</span>
                  </Link>
                ))}
              </div>
            )}
        </Panneau>
      )}
    </div>
  );
}
